import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/editor/slice'
import { editorSelectors } from 'store/features/editor/selectors'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { withAppThunks } from 'store/with-app-thunks'
import { selectionThunks } from 'store/features/editor/thunks/selection'
import { IndexedDB } from 'services/indexed-db'
import { ITableConfigIndexedDB } from 'services/indexed-db/types'
import {
  AI_AVATAR_LANDING_DATA_STORAGE_KEY,
  IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY,
  PROJECT_MEDIA_TYPES,
  UPGRADE_POPUP_OPENER_REF
} from 'consts'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { getAvatarAssetsForAspectRatio } from 'store/features/aiAvatar/utils'
import type { IUploadMediaItem } from 'types/media'
import { getLibraryFonts_API } from 'api'
import { ReferenceMediaImageType } from 'types/aiVideo'
import { AvatarSource } from 'store/types/aiAvatar'
import { ReduxThunkError } from 'utils/errors'
import { captureException } from '@sentry/react'

const { actions } = slice

const LANDING_DB_NAME = 'image-to-video-ai-landing'
const LANDING_TABLE_NAME = 'image-to-video-ai-data'
const LANDING_TABLE_CONFIG: ITableConfigIndexedDB = {
  name: LANDING_TABLE_NAME
}

interface IImageToVideoAiLandingData {
  prompt?: string
  selectedStyleId?: number | null
  selectedAspectRatio?: string | null
  uploadedImage?: Blob | null
  uploadedImageName?: string | null
  uploadedImageType?: string | null
}

// Public avatars handed off from the landing are always selected at 16:9 — we
// use the avatar's 16:9 image and commit that aspect ratio.
const AVATAR_16_9 = '16:9'

const AVATAR_LANDING_DB_NAME = 'ai-avatar-landing'
const AVATAR_LANDING_TABLE_NAME = 'ai-avatar-data'
const AVATAR_LANDING_TABLE_CONFIG: ITableConfigIndexedDB = {
  name: AVATAR_LANDING_TABLE_NAME
}

// Data the landing page hands off to the AI Avatar tab via IndexedDB (image
// can't fit in the URL). The avatar arrives either as an uploaded image (Blob,
// re-uploaded here) or as a pre-selected public avatar (id + url). `speechText`
// is the avatar's prompt — the only text field it has.
interface IAiAvatarLandingData {
  speechText?: string
  voiceLangCode?: string
  voiceSpeakerId?: number | null
  // Pre-selected (public) avatar branch. The landing serializes the avatar id as
  // a number, so accept both and coerce to the string id the store expects.
  avatarId?: number | string | null
  avatarUrl?: string | null
  // Avatar gender — used to auto-pick a matching voice when no speaker is given.
  avatarGender?: string | null
  // Uploaded image branch.
  uploadedImage?: Blob | null
  uploadedImageName?: string | null
  uploadedImageType?: string | null
}

const setActiveSidebarTabAndCloseOverlay =
  (tabKey: string) => (dispatch: TAppDispatch, getState: TGetState) => {
    const activeOverlay = editorSelectors.activeAsideOverlayKey(getState())

    if (activeOverlay) {
      // Use toggle to close if same overlay is open
      dispatch(actions.toggleAsideOverlayContent(activeOverlay))
    } else {
      dispatch(actions.setAsideOverlayContent(null))
    }

    dispatch(actions.setActiveSidebarTab(tabKey))
  }

const getLibraryFonts = () => async (dispatch: TAppDispatch) => {
  const response = await getLibraryFonts_API()
  dispatch(actions.setLibraryFonts(response.data))
}

const setToDefaultTab = () =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const formCleaningMap = {
      [SUB_SIDEBAR_KEYS.AI_IMAGE]: appThunks.aiImageThunks.resetGenerateForm,
      [SUB_SIDEBAR_KEYS.AI_VIDEO]: appThunks.aiVideoThunks.clearGenerationParams
    }
    const { sidebarItems } = await import('containers/Sidebar/consts')
    dispatch(actions.setActiveSidebarTab(sidebarItems[0].key))
    const formCleaningThunk = formCleaningMap[sidebarItems[0].key]
    if (formCleaningThunk) {
      dispatch(formCleaningThunk())
    }
  })

const applyImageToVideoAiLandingData = (isMobile: boolean) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const db = IndexedDB.getInstance(LANDING_DB_NAME, [LANDING_TABLE_CONFIG])
      const data = await db.readRecord<IImageToVideoAiLandingData>(
        LANDING_TABLE_NAME,
        IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY
      )

      if (!data) {
        return
      }

      if (data.prompt) {
        dispatch(appThunks.aiVideoThunks.setPrompt(data.prompt))
      }
      if (!aiVideoSelectors.hasVideoModels(getState())) {
        await dispatch(appThunks.aiVideoThunks.getAIVideoModels())
      }

      if (data.selectedAspectRatio) {
        const state = getState()
        const currentModel = aiVideoSelectors.selectedModelCode(state)
        if (currentModel) {
          dispatch(
            appThunks.aiVideoThunks.updateSelectedModel({
              modelName: currentModel,
              aspectRatio: data.selectedAspectRatio
            })
          )
        }
      }

      if (data.uploadedImage && data.uploadedImage instanceof Blob) {
        const file = new File(
          [data.uploadedImage],
          data.uploadedImageName ?? 'uploaded-image.png',
          { type: data.uploadedImageType ?? 'image/png' }
        )
        const uploadItem: IUploadMediaItem = {
          id: `landing-${Date.now()}`,
          file,
          type: PROJECT_MEDIA_TYPES.image
        }
        await dispatch(
          appThunks.aiVideoThunks.uploadFrameImage(
            ReferenceMediaImageType.FIRST_FRAME,
            [uploadItem]
          )
        )
      }

      if (data.selectedStyleId !== null) {
        dispatch(
          appThunks.aiVideoThunks.setRefImageSelectedStyleId(
            data.selectedStyleId
          )
        )
      }

      await db.deleteRecord(
        LANDING_TABLE_NAME,
        IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY
      )

      dispatch(
        appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
      )
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(true))
      }
    }
  )

const applyAiAvatarLandingData = (isMobile: boolean) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      // The `?selectedTab=aiAvatar` param is itself the handoff signal, so switch
      // to the AI Avatar tab up front — before reading IndexedDB and before the
      // awaited (slow, possibly failing) avatar upload / voice-catalog fetch
      // below. Doing it last meant a missing record or any rejected await left
      // the tab on its default (AI_VIDEO), so the handoff visibly did nothing.
      dispatch(
        appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_AVATAR)
      )
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(true))
      }

      const db = IndexedDB.getInstance(AVATAR_LANDING_DB_NAME, [
        AVATAR_LANDING_TABLE_CONFIG
      ])
      const data = await db.readRecord<IAiAvatarLandingData>(
        AVATAR_LANDING_TABLE_NAME,
        AI_AVATAR_LANDING_DATA_STORAGE_KEY
      )

      if (!data) {
        return
      }

      if (data.speechText) {
        dispatch(appThunks.aiAvatarThunks.setSpeechText(data.speechText))
      }

      // Avatar and voice are applied independently and guarded: a failed avatar
      // upload must not stop the voice from being applied (or vice versa), and
      // neither must block the record cleanup below — otherwise a stale handoff
      // record would survive and re-apply on the next load.
      try {
        // Pre-selected public avatar: the landing hands off only the avatar id,
        // so resolve it from the editor's own public-avatars catalog — never
        // upload, never use a handed-off url. Match `avatarId === public avatar
        // id` and use the avatar's 16:9 image. Only re-upload the dropped image
        // when there's no id.
        if (data.avatarId) {
          await dispatch(appThunks.aiAvatarThunks.getPublicAvatars())
          const state = getState()
          // Coerce to string: public avatar ids arrive as numbers, but the store's
          // avatar id is a string and the gender resolver matches
          // `String(publicAvatar.id) === avatar.id`.
          const avatarId = String(data.avatarId)
          const match = aiAvatarSelectors
            .publicAvatarsData(state)
            ?.find(pa => String(pa.id) === avatarId)
          if (match) {
            const { url } = getAvatarAssetsForAspectRatio(match, AVATAR_16_9)
            dispatch(
              appThunks.aiAvatarThunks.setAvatar({
                id: avatarId,
                url,
                source: AvatarSource.PUBLIC
              })
            )
            // The selected url is the 16:9 variant, so commit that ratio too
            // (Redux holds a ratio only while a public avatar is selected).
            dispatch(appThunks.aiAvatarThunks.setAvatarAspectRatio(AVATAR_16_9))
          }
        } else if (data.uploadedImage && data.uploadedImage instanceof Blob) {
          const file = new File(
            [data.uploadedImage],
            data.uploadedImageName ?? 'avatar-image.png',
            { type: data.uploadedImageType ?? 'image/png' }
          )
          const uploadItem: IUploadMediaItem = {
            id: `landing-avatar-${Date.now()}`,
            file,
            type: PROJECT_MEDIA_TYPES.image
          }
          await dispatch(appThunks.aiAvatarThunks.uploadAvatar([uploadItem]))
        }
      } catch (error) {
        captureException(new ReduxThunkError(error))
      }

      try {
        // An exact speaker handed off from the landing wins outright. Otherwise,
        // when the avatar's gender is given, auto-pick a matching voice via the
        // same `selectSpeakerByGender` path a public-avatar click uses — this runs
        // even when no language was handed off (it picks for the current default
        // language). With neither speaker nor gender, fall back to the default
        // first-usable-speaker auto-pick for the chosen language.
        if (data.voiceLangCode && typeof data.voiceSpeakerId === 'number') {
          await dispatch(
            appThunks.aiAvatarThunks.setVoiceSelection(
              data.voiceLangCode,
              data.voiceSpeakerId
            )
          )
        } else if (data.avatarGender) {
          if (data.voiceLangCode) {
            dispatch(
              appThunks.aiAvatarThunks.setVoiceLangCode(data.voiceLangCode)
            )
          }
          await dispatch(
            appThunks.aiAvatarThunks.selectSpeakerByGender(data.avatarGender)
          )
        } else if (data.voiceLangCode) {
          await dispatch(
            appThunks.aiAvatarThunks.setVoiceLanguage(data.voiceLangCode)
          )
        }
      } catch (error) {
        captureException(new ReduxThunkError(error))
      }

      await db.deleteRecord(
        AVATAR_LANDING_TABLE_NAME,
        AI_AVATAR_LANDING_DATA_STORAGE_KEY
      )
    }
  )

const openUpgradePopup = (type: string) => () => {
  window.openUpgradePopup({
    type,
    openerRef: UPGRADE_POPUP_OPENER_REF
  })
}

export const editorThunks = {
  ...actions,
  ...selectionThunks,
  openUpgradePopup,
  setActiveSidebarTabAndCloseOverlay,
  setToDefaultTab,
  applyImageToVideoAiLandingData,
  applyAiAvatarLandingData,
  getLibraryFonts
}
